import { useEffect, useState } from "react";
import { listLocationCities } from "@/api/locations";
import { logger, serializeError } from "@/lib/logger";

export interface CityOption {
  city: string;
  country: string;
}

let cache: CityOption[] | null = null;

/**
 * Distinct city/country pairs across all locations (every city that has ever
 * participated, onsite or not) — drives the landing notify form's city
 * typeahead. Broader than useOnsiteLocations (onsite-only). Reads v_location and
 * de-dupes (the small list rarely changes; cached at module scope).
 */
export function useAllCities(): { cities: CityOption[]; loading: boolean } {
  const [cities, setCities] = useState<CityOption[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);

  useEffect(() => {
    if (cache !== null) return;
    let cancelled = false;

    (async () => {
      const { data, error } = await listLocationCities();
      if (cancelled) return;
      if (error || !data) {
        if (error) logger.error("useAllCities: failed to load v_location", serializeError(error));
        setLoading(false);
        return;
      }
      const seen = new Map<string, CityOption>();
      for (const r of data) {
        if (r.city && r.country) seen.set(`${r.city}|${r.country}`, { city: r.city, country: r.country });
      }
      cache = [...seen.values()];
      setCities(cache);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { cities, loading };
}
